<template>
  <Popup
    v-if="stepState === 'invite'"
    :model-value="modelValue"
    popup-header="You’re invited to become a Trusted Sitter"
    popup-size="md"
    header-class="text-h3 sm:text-h2 sm:pt-16"
    custom-classes="sm:!px-40 sm:!pb-40"
    :close-on-outside-click="false"
    :hide-close-button="true"
    @close="onRemindLater"
  >
    <TrustedCaregiverInviteContent
      :submitting="submitting"
      :error-message="errorMessage"
      @decline="onDecline"
      @accept="onAccept"
      @remind-later="onRemindLater"
    />
  </Popup>

  <Popup
    v-else-if="stepState === 'declineConfirm'"
    :model-value="modelValue"
    popup-header="Wait, are you sure?"
    popup-size="lg"
    header-class="text-h3 sm:text-h2 sm:pt-16"
    custom-classes="sm:!px-40 sm:!pb-40"
    :close-on-outside-click="false"
    :hide-close-button="true"
    @close="onRemindLater"
  >
    <TrustedCaregiverDeclineConfirmContent
      :submitting="submitting"
      :error-message="errorMessage"
      @confirm-decline="onConfirmDecline"
      @accept="onAccept"
      @remind-later="onRemindLater"
    />
  </Popup>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import Popup from '@/components/popup/Popup.vue';
import { getSharedApolloClient } from '@/lib/apollo_setup';
import {
  RESPOND_TO_TRUSTED_CAREGIVER_INVITE,
  type TrustedCaregiverInviteResponse,
} from '@/graphql/provider/trusted_caregiver/mutations';
import { snoozeTrustedCaregiverInvite } from './trustedCaregiverInviteSnooze';
import TrustedCaregiverInviteContent from './TrustedCaregiverInviteContent.vue';
import TrustedCaregiverDeclineConfirmContent from './TrustedCaregiverDeclineConfirmContent.vue';

defineOptions({ name: 'TrustedCaregiverPopup' });

const props = defineProps<{
  modelValue: boolean;
  accountId: string | number;
}>();

const emit = defineEmits<{
  accept: [];
  decline: [];
  remindLater: [];
}>();

const submitting = ref<TrustedCaregiverInviteResponse | null>(null);
const errorMessage = ref('');
const remindingLater = ref(false);
// Popup emits `close` whenever it shuts (including when the parent flips modelValue
// after accept/decline). Only X, Escape, and "Remind me later" should snooze.
const snoozeOnClose = ref(true);

type Step = 'invite' | 'declineConfirm';
const stepState = ref<Step>('invite');

watch(
  () => props.modelValue,
  (open) => {
    if (open) {
      submitting.value = null;
      errorMessage.value = '';
      remindingLater.value = false;
      snoozeOnClose.value = true;
      stepState.value = 'invite';
    }
  }
);

function onRemindLater() {
  if (submitting.value || remindingLater.value) return;
  if (!snoozeOnClose.value) {
    snoozeOnClose.value = true;
    return;
  }
  remindingLater.value = true;
  snoozeTrustedCaregiverInvite(props.accountId);
  emit('remindLater');
}

function onDecline() {
  if (submitting.value) return;
  errorMessage.value = '';
  stepState.value = 'declineConfirm';
}

async function onConfirmDecline() {
  if (submitting.value) return;

  const succeeded = await respondToInvite('DECLINE');
  if (!succeeded) return;

  snoozeOnClose.value = false;
  emit('decline');
}

async function onAccept() {
  if (submitting.value) return;

  const succeeded = await respondToInvite('ACCEPT');
  if (!succeeded) return;

  snoozeOnClose.value = false;
  emit('accept');
}

async function respondToInvite(response: TrustedCaregiverInviteResponse): Promise<boolean> {
  submitting.value = response;
  errorMessage.value = '';

  try {
    const { data } = await getSharedApolloClient().mutate({
      mutation: RESPOND_TO_TRUSTED_CAREGIVER_INVITE,
      variables: { response },
    });

    if (!data?.respondToTrustedCaregiverInvite) {
      throw new Error('Trusted Caregiver invite response was not recorded');
    }

    // Accept must return an onboarding status; decline intentionally returns null.
    if (response === 'ACCEPT' && !data.respondToTrustedCaregiverInvite.status) {
      throw new Error('Trusted Caregiver invite was not accepted');
    }

    return true;
  } catch (error) {
    console.error('Unable to respond to Trusted Caregiver invite', error);
    errorMessage.value = 'Something went wrong. Please try again.';
    return false;
  } finally {
    submitting.value = null;
  }
}
</script>
